import React from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import lodashGet from "lodash/get";

import { BackButton } from "jazasoft";
import PageFooter from "jazasoft/lib/mui/layout/PageFooter";
import ExcelIcon from "mdi-material-ui/FileExcel";
import { isEmpty, isTrue, listFromCsv, toFixed } from "../../../utils/helpers";
import MuiTable from "../../../components/MuiTable";
import { convertTableToCell } from "../../../utils/excelHelper";
import { useTenant, useMutation, useQuery, useDeepMemo, useUnit } from "../../../hooks";
import { colorOrange, colorTomatoRed } from "../../../utils/colors";
import { makeStyles } from "@material-ui/core";

const getPendingFrom = cutCompletionDate => {
  const diffInMillis = new Date().getTime() - cutCompletionDate;
  return diffInMillis > 0 ? toFixed(diffInMillis / (1000 * 60 * 60 * 24), 0) : 0;
};

const choicesPendingFrom = [
  { id: "0-10", name: "Less Than 10 Days", start: 0, end: 10 },
  { id: "10-20", name: "From 10-20 Days", start: 10, end: 20 },
  { id: "20-30", name: "From 20-30 Days", start: 20, end: 30 },
  { id: "30-90", name: "From 30-90 Days", start: 30, end: 90 },
  { id: "90-100000", name: "More Than 90 Days", start: 90, end: 100000 }
];

const getSearchText = filterValues => {
  const { unitId, productIds, seasonId, customerIds } = filterValues;
  let searchItems = [];
  if (!isEmpty(unitId)) {
    searchItems.push(`unitId=='${unitId}'`);
  }
  if (!isEmpty(productIds)) {
    searchItems.push(`product.id=in=(${productIds.join(",")})`);
  }
  if (!isEmpty(seasonId)) {
    searchItems.push(`order.season.id=='${seasonId}'`);
  }
  if (!isEmpty(customerIds)) {
    searchItems.push(`order.customer.id=in=(${customerIds.join(",")})`);
  }
  return searchItems.join(";");
};

const useStyles = makeStyles({
  content: {
    padding: '1.5em'
  }
})

const DailyReport = props => {
  const classes = useStyles();
  // const { unitId, productList, season, customerList, pendingSince } = props?.filter;

  const i18nMessage = useSelector(state => state?.i18n?.messages || {});
  const tenant = useTenant();
  const { allUnitList } = useUnit();
  const [download] = useMutation({ rawResponse: true });

  const searchText = getSearchText(props.filter || {});

  const { data: pendingFlowList = [] } = useQuery({ url: "closure-pending-report", params: { search: isEmpty(searchText) ? "" : searchText } });

  const columns = useDeepMemo(() => {
    let cols = [
      {
        dataKey: "pendingSince",
        title: "Pending Since",
        render: value => (
          <div
            style={{
              backgroundColor: value >= 90 ? colorTomatoRed.dark : colorOrange.light,
              color: "white",
              textAlign: "center",
              padding: "4px 12px",
              borderRadius: 4
            }}>
            {value ? `${value} days` : "-"}
          </div>
        ),
      },
      { dataKey: 'pending', title: 'Pending Since', filterOptions: { filter: true }, hidden: true },
      { dataKey: "state", title: "State", filterOptions: { filter: true } },
      { dataKey: "unit", title: "Unit", filterOptions: { filter: true } },
      { dataKey: "poRef", title: "ERP No" },
      { dataKey: "fpo", title: "FPO No" },
      { dataKey: "customer", title: "Customer", filterOptions: { filter: true } },
      { dataKey: "season", title: "Season", filterOptions: { filter: true } },
      { dataKey: "product", title: "Product", filterOptions: { filter: true } },
      { dataKey: "style", title: "Style Name" },
      { dataKey: "color", title: "Style Color" },
      { dataKey: "part", title: "Part" },
      { dataKey: "lotValue", title: "Lot" },
      { dataKey: "inseams", title: "inseams" },
      { dataKey: "destinations", title: "Destinations" },
      { dataKey: "deliveries", title: "deliveries" },
      { dataKey: "delModes", title: "Delivery Modes" },
      { dataKey: "customerFlowRefs", title: "BPO" },
      { dataKey: "flowRefs", title: "Line ID" },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "fpoQty", title: "FPO Qty" },
      { dataKey: "cutQty", title: "Cut Qty" },
      { dataKey: "cutQtyPercent", title: "Cut Qty (%)", render: value => `${value} %` },
      { dataKey: "cutCompletionDate", title: "Cut Completion Date", render: value => (value ? format(value, "MMM d, yyyy") : "-") }
    ];

    if (pendingFlowList.every(e => isEmpty(e.destinations))) {
      cols = cols.filter(c => c.dataKey !== "destinations");
    }
    if (pendingFlowList.every(e => isEmpty(e.inseams))) {
      cols = cols.filter(c => c.dataKey !== "inseams");
    }
    if (pendingFlowList.every(e => isEmpty(e.deliveries))) {
      cols = cols.filter(c => c.dataKey !== "deliveries");
    }
    if (pendingFlowList.every(e => isEmpty(e.delModes))) {
      cols = cols.filter(c => c.dataKey !== "delModes");
    }
    if (pendingFlowList.every(e => isEmpty(e.customerFlowRefs))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRefs");
    }
    if (pendingFlowList.every(e => isEmpty(e.flowRefs))) {
      cols = cols.filter(c => c.dataKey !== "flowRefs");
    }

    return cols;
  }, [pendingFlowList]);

  const rows = useDeepMemo(() => {
    return pendingFlowList.map(row => ({
      ...row,
      unit: allUnitList?.find(u => u.unitId === row.unitId)?.name || row.unitId,
      inseams: listFromCsv(row.inseams).join(", "),
      destinations: listFromCsv(row.destinations).join(", "),
      deliveries: listFromCsv(row.deliveries).join(", "),
      delModes: listFromCsv(row.delModes).join(", "),
      flowRefs: listFromCsv(row.flowRefs).join(", "),
      customerFlowRefs: listFromCsv(row.customerFlowRefs).join(", "),
      cutQtyPercent: toFixed(row.orderQty > 0 ? (row.cutQty * 100) / row.orderQty : 0, 2),
      pendingSince: isEmpty(row.cutCompletionDate) ? undefined : getPendingFrom(row.cutCompletionDate)
    })).map(row => ({ ...row, pending: isEmpty(row.pendingSince) ? '' : choicesPendingFrom.find(c => row.pendingSince >= c.start && row.pendingSince < c.end)?.name }));
  }, [pendingFlowList, allUnitList]);




  const onFooterActionClick = (e, action, rows, filterValues, onActionComplete) => {
    const fileName = `Closure_Pending_Report.xlsx`.toUpperCase();

    const columnList = columns.filter(c => !isTrue(c.hidden)).map((c, idx) => ({
      ...c,
      colNo: idx + 1,
      title: lodashGet(i18nMessage, `resources.report.closurePending.${c.dataKey}`, c.title),
      type: c.dataKey === "cutCompletionDate" ? "date" : null
    }));

    const rowList = rows
      .filter(row => Object.keys(filterValues).filter(key => !isEmpty(filterValues[key])).every(key => row[key] === filterValues[key]))
      .map((row, idx) => ({
        rowNo: idx + 1,
        cellList: columnList.map(c => ({ dataKey: c.dataKey, value: row[c.dataKey] }))
      }));

    const maxColNo = columnList?.length;
    let filterColumns = [
      { dataKey: "unit", title: "Unit" },
      { dataKey: "season", title: "Season" },
      { dataKey: "product", title: "Product" },
      { dataKey: "customer", title: "Customer" },
      { dataKey: "pendingSince", title: "Pending Since" },
      { dataKey: "state", title: "Status" },
    ];

    let filterRows = [
      filterValues
    ];

    const filterCells = convertTableToCell(filterColumns, filterRows, 3, maxColNo);
    const unit = allUnitList.find(e => e.name === filterValues?.unit) || {};
    
    const cellList = [
      {
        rowNo: 1,
        colNo: 1,
        value: unit?.displayName || unit?.name || filterValues?.unit || tenant?.name || "",
        colEnd: maxColNo,
        cellStyle: { hAlign: "center", border: false, bold: true, fontHeight: 24 },
        cellHeight: 32
      },
      {
        rowNo: 2,
        colNo: 1,
        value: "Closure Pending Report",
        colEnd: maxColNo,
        cellStyle: { hAlign: "center", border: false, fontHeight: 18 },
        cellHeight: 32
      },
      ...filterCells
    ];

    const options = {
      url: `export/excel`,
      method: "post",
      responseType: "arraybuffer",
      data: [{ sheetNo: 0, sheetName: "Closure Pending Report", excelData: { columnList, rowList, cellList } }],
      params: { filename: fileName }
    };

    download(options, {
      onSuccess: response => {
        if (response.status === 200) {
          const header = response.headers["content-disposition"];
          var filename = header ? header.match(/filename="(.+)"/)[1] : fileName;

          let blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        }
        onActionComplete && onActionComplete(rows);
      },
      onFailure: () => onActionComplete && onActionComplete(rows)
    });
  };


  return (
    <div className={classes.content} >
      <div style={{ marginTop: 10 }}>
        <MuiTable
          title="Order Closure Pending Report"
          columns={columns}
          rows={rows}
          searchable={true}
          searchKeys={["poRef", "fpo", "style", 'fpo', "flowRefs", "customerFlowRefs"]}
          pageable={true}
          footerActions={[{ name: 'download', tooltip: 'Download', icon: <ExcelIcon />, options: { style: { color: 'green' }, variant: 'outlined' } }]}
          onFooterActionClick={onFooterActionClick}
          i18nKey="resources.report.closurePending"
        />
      </div>


      <PageFooter>
        <BackButton variant="outlined" color="primary" />
      </PageFooter>
    </div>
  );
};



export default DailyReport;
